import React, { ReactNode } from 'react';
import { z } from 'zod';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { useQuery } from '@apollo/client';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CustomDialog } from 'components/shared/Dialog';
import { Box, Button, Stack, Typography } from '@mui/material';
import { BusinessRule } from '@trnsact/business-criteria';
import { AftermarketProductType, MarkupType } from '@trnsact/trnsact-shared-types';
import { MARKUP_TYPE } from '@trnsact/business-criteria/dist/src/models/rule-event/aftermarket/markup-type';
import { AFTERMARKET_PRODUCT_EVENTS } from '@trnsact/business-criteria/dist/src/models/rule-event/aftermarket/aftermarket-product-events';
import {
  InputField,
  RadioGroupField,
  AutocompleteField,
  CurrencyInputField,
  InterestInputField,
} from 'components/form';
import { productsTypesOptions } from '../../constants';
import { createCustomProductSchema } from '../../model/schema';
import { aftermarketProductsApi } from '../../../aftermarket/products/api';
import { Add } from '@mui/icons-material';
import { GetPartnerLinksResponse, GetPartnerLinksVariables } from '../../../aftermarket';

export const CreateProposalProductDialog = () => {
  const { isOpen, handleClose, data } = useModal(ModalsKeys.ProposalCreateProductDialog);
  const { handleOpen: handleOpenCreatePartnerDialog } = useModal(ModalsKeys.AftermarketCreatePartnerDialog);

  const { data: linksResponse, loading: isOptionsLoading } = useQuery<
    GetPartnerLinksResponse,
    GetPartnerLinksVariables
  >(aftermarketProductsApi.getPartnerLinks, {
    variables: { accountId: data.accountId },
  });

  const handleCreateProduct = async (formData: z.infer<typeof createCustomProductSchema>) => {
    handleClose();

    const rule = new BusinessRule({
      name: 'Base Configuration',
      eventParams: {
        type: AFTERMARKET_PRODUCT_EVENTS.AFTERMARKET_PRODUCT_QUALIFIED,
        params: {
          RATE_FACTOR: 0,
          PRICING_MAX: 0,
          PRICING_MIN: 0,
          DEALER_COST: formData.cost,
          SUGGESTED_RETAIL_PRICE: formData.retailPrice,
          MARKUP: {
            AMOUNT: formData.markup.value,
            TYPE: formData.markup.type === MarkupType.Flat ? MARKUP_TYPE.FIXED : MARKUP_TYPE.PERCENTAGE,
          },
        },
      },
    });

    await data.onCreate({
      productName: formData.name,
      sourceOfCreation: 'manual',
      productCategory: formData.type.value,
      partnerLinkIds: [formData.vendor.value],
      productType: AftermarketProductType.Product,
      productDescriptionInternal: formData.description || null,
      criteria: [
        {
          name: 'Base Configuration',
          event: rule.Event.getEvent(),
          conditions: { formRules: [], jsonRules: { conditions: { all: [] } } },
        },
      ],
    });
  };

  const form = useForm<z.infer<typeof createCustomProductSchema>>({
    mode: 'all',
    resolver: zodResolver(createCustomProductSchema),
    defaultValues: {
      cost: 0,
      name: '',
      retailPrice: 0,
      description: '',
      vendor: undefined,
      type: productsTypesOptions[0],
      markup: { value: 0, type: MarkupType.Flat },
    },
  });

  const calcMarkup = () => {
    const { retailPrice, cost } = form.getValues();
    form.setValue('markup.value', retailPrice - cost);
  };

  const calcRetailPrice = () => {
    const { markup, cost } = form.getValues();

    if (markup.type === MarkupType.Percentage) form.setValue('retailPrice', cost * (1 + markup.value / 100));
    else form.setValue('retailPrice', cost + markup.value);
  };

  const markupType = useWatch({ control: form.control, name: 'markup.type' });

  const markupField: Record<MarkupType, ReactNode> = {
    [MarkupType.Flat]: (
      <CurrencyInputField
        allowNegative
        label="Markup ($)"
        name="markup.value"
        control={form.control}
        textFieldProps={{ required: true }}
        customOnChange={(nextValue, name) => {
          form.setValue(name, nextValue);
          calcRetailPrice();
        }}
      />
    ),
    [MarkupType.Percentage]: (
      <InterestInputField
        allowNegative
        label="Markup (%)"
        control={form.control}
        name="markup.value"
        textFieldProps={{ required: true }}
        customOnChange={(nextValue, name) => {
          form.setValue(name, nextValue);
          calcRetailPrice();
        }}
      />
    ),
  };

  const linkOptions = (linksResponse?.aftermarketDealerPartnerLink ?? []).map(link => ({
    value: link.partnerLinkId,
    label: link.name || 'Partner Vendor',
  }));

  return (
    <CustomDialog
      maxWidth="lg"
      isOpen={isOpen}
      label="Create product"
      onClose={handleClose}
      actions={
        <>
          <Button onClick={handleClose}>Cancel</Button>

          <Button variant="contained" onClick={form.handleSubmit(handleCreateProduct)}>
            Create
          </Button>
        </>
      }
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="body1">Common information</Typography>

        <Button
          size="small"
          variant="outlined"
          startIcon={<Add />}
          onClick={() => {
            handleClose();
            handleOpenCreatePartnerDialog();
          }}
        >
          Add New Vendor
        </Button>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', alignItems: 'baseline' }}>
        <AutocompleteField
          name="vendor"
          options={linkOptions}
          control={form.control}
          loading={isOptionsLoading}
          textFieldProps={{ label: 'Vendor', placeholder: 'Select vendor', required: true }}
        />

        <AutocompleteField
          name="type"
          control={form.control}
          options={productsTypesOptions}
          textFieldProps={{ label: 'Type', placeholder: 'Select product type', required: true }}
        />

        <InputField
          name="name"
          label="Product name"
          control={form.control}
          inputProps={{ placeholder: 'Enter product name', required: true }}
        />

        <InputField
          name="description"
          control={form.control}
          label="Product description"
          inputProps={{ placeholder: 'Enter product description' }}
        />

        <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: '0.5rem', gridColumn: '1 / -1' }}>
          <Typography variant="body1">Product Price</Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
            <CurrencyInputField
              name="cost"
              label="Cost"
              control={form.control}
              textFieldProps={{ required: true, placeholder: 'Enter product cost' }}
              customOnChange={(nextValue, name) => {
                form.setValue(name, nextValue);
                calcRetailPrice();
              }}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem', alignItems: 'baseline' }}>
              {markupField[markupType]}

              <RadioGroupField
                row
                name="markup.type"
                control={form.control}
                extraAction={calcRetailPrice}
                options={[
                  { value: MarkupType.Flat, label: '$' },
                  { value: MarkupType.Percentage, label: '%' },
                ]}
              />
            </Box>

            <CurrencyInputField
              name="retailPrice"
              label="Retail price"
              control={form.control}
              textFieldProps={{ required: true, placeholder: 'Enter retail price' }}
              customOnChange={(nextValue, name) => {
                form.setValue(name, nextValue);
                calcMarkup();
              }}
            />
          </Box>
        </Box>
      </Box>
    </CustomDialog>
  );
};
